import { useState, useEffect } from 'react'
import { useCrosswordStore } from '../store'
import { findAllCrossingsForPair } from '../lib/search'
import { findPlacementOptions, type PlacePairInput } from '../lib/constructor'
import {
  findSolutionsAtDepthDetailed,
  type Solution,
  type HelperStep,
  type BackwardsSearchFilters,
} from '../lib/backwardsSearch'
import { generateId, getLetters } from '../lib/pairings'
import { StatusIcon, StatusBadge, PAIRING_STATUS_CONFIG } from './StatusFilter'
import { CrosswordPair } from './CrosswordText'
import { TagEditor } from './TagEditor'
import { TrashIcon } from './Icons'
import { PAIRING_RATING_LABELS, type Pairing, type ClueStatus, type PairingStatus, type PairingRating, type ConstructorGrid, type PreviewPlacement } from '../types'

type Props = {
  // The Pairing record. Null when the surface wants an "ephemeral" pair —
  // word IDs only, no metadata committed. The record is materialized on the
  // first metadata change (status / rating / clue / tag). Used by the Pairs
  // page so just clicking through candidates doesn't pollute the table.
  pairing: Pairing | null
  // Required when pairing is null. Used to look up the words and to call
  // getOrCreatePairing on first action. Ignored when pairing is non-null.
  ephemeralPair?: { wordAId: string; wordBId: string } | undefined
  grid: ConstructorGrid | null
  onClose: () => void
  onShowWord?: (wordId: string) => void
  onShowCrossings: () => void
  crossingsOpen: boolean
  filters?: BackwardsSearchFilters
  onPreviewChange?: (preview: PreviewPlacement | null) => void
  onPreviewPlacementsChange?: (placements: PreviewPlacement[]) => void
  onPlacePair?: (wordAText: string, wordBText: string, row: number, col: number, direction: 'across' | 'down') => void
}

type SectionKey = 'clue' | 'placements' | 'solutions'

function SectionHeader({
  label,
  open,
  onToggle,
  trailing,
}: {
  label: string
  open: boolean
  onToggle: () => void
  trailing?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-y border-gray-100 bg-gray-50/50">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
      >
        <span className={`text-[9px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        {label}
      </button>
      {trailing}
    </div>
  )
}

export function PairDetailColumn({
  pairing,
  ephemeralPair,
  grid,
  onClose,
  onShowWord,
  onShowCrossings,
  crossingsOpen,
  filters,
  onPreviewChange,
  onPreviewPlacementsChange,
  onPlacePair,
}: Props) {
  const { words, pairings, updatePairingClue, addPairingDraftClue, deletePairingDraftClue, updatePairingStatus, updatePairingRating, deletePairing, setSelectedPairing, placeManyOnGrid, setPairingTags, getOrCreatePairing } = useCrosswordStore()

  // Word IDs used for both modes. When `pairing` is non-null we read off it;
  // when null we fall back to the explicit ephemeralPair payload.
  const wordAId = pairing?.wordAId ?? ephemeralPair?.wordAId
  const wordBId = pairing?.wordBId ?? ephemeralPair?.wordBId
  // Stable identity key for state-reset effects.
  const pairKey = pairing?.id ?? (wordAId && wordBId ? `ephemeral:${wordAId}:${wordBId}` : '')

  const [clueText, setClueText] = useState(pairing?.clue?.text || '')
  const [clueStatus, setClueStatus] = useState<ClueStatus>(pairing?.clue?.status || 'draft')
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    clue: true,
    placements: true,
    solutions: false,
  })
  // Per-depth solution cache. Each depth runs on demand when the user clicks
  // its accordion header — caches the result so reopening is instant. Resets
  // when the target or grid changes.
  const [solutionsByDepth, setSolutionsByDepth] = useState<Map<number, { solutions: Solution[]; truncated: boolean }>>(new Map())

  const wordA = wordAId ? words.find(w => w.id === wordAId) : undefined
  const wordB = wordBId ? words.find(w => w.id === wordBId) : undefined

  useEffect(() => {
    setClueText(pairing?.clue?.text || '')
    setClueStatus(pairing?.clue?.status || 'draft')
  }, [pairKey, pairing?.clue?.text, pairing?.clue?.status])

  const crossingsCount = (() => {
    if (!wordA || !wordB) return 0
    return findAllCrossingsForPair(wordA, wordB, words, pairings).length
  })()

  // Reset cached results when the target / grid changes.
  useEffect(() => {
    setSolutionsByDepth(new Map())
  }, [pairKey, grid])

  // Direct placements (always shown — cheap, conflict / adjacency / wildcard
  // rules already enforced by findPlacementOptions).
  const placementOptions = (() => {
    if (!wordA || !wordB || !grid) return []
    return findPlacementOptions(grid, wordA.text, wordB.text, { maxResults: 100, ...(pairing ? { pairingId: pairing.id } : {}) })
  })()

  const computeForDepth = (depth: number) => {
    if (!wordA || !wordB || !grid) return
    const result = findSolutionsAtDepthDetailed(grid, wordA, wordB, words, pairings, depth, { filters })
    setSolutionsByDepth(prev => {
      const next = new Map(prev)
      next.set(depth, { solutions: result.solutions, truncated: result.truncated })
      return next
    })
  }

  // Materialize the Pairing record on first metadata change. Returns the
  // resolved id, or null if creation failed (e.g. words from same list).
  // Use this in any handler that writes via the store. Reads should use
  // optional chaining on `pairing` directly.
  const ensureRecord = (): string | null => {
    if (pairing) return pairing.id
    if (!wordAId || !wordBId) return null
    const created = getOrCreatePairing(wordAId, wordBId)
    return created?.id ?? null
  }

  if (!wordA || !wordB) {
    return (
      <div className="p-4 text-gray-500 bg-white rounded-xl shadow-sm h-full">
        Words not found
      </div>
    )
  }

  const lettersA = getLetters(wordA)
  const lettersB = getLetters(wordB)

  const persistClue = (text: string, status: ClueStatus) => {
    // Empty text clears the clue. For an ephemeral pair we also skip the
    // materialize-on-action path — no point creating a record just to clear
    // a clue that doesn't exist.
    const trimmed = text.trim()
    if (!trimmed && !pairing) return
    const id = ensureRecord()
    if (id) updatePairingClue(id, text, status)
  }

  const handleDeletePairing = () => {
    if (!pairing) return
    if (confirm('Delete this pairing?')) {
      deletePairing(pairing.id)
      setSelectedPairing(null)
    }
  }

  const toggleSection = (key: SectionKey) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }))

  const isDraft = clueStatus === 'draft'

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-white rounded-xl shadow-sm">
      {/* Sticky header — status buttons + close */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 shrink-0 border-b border-gray-100">
        <div className="flex items-center gap-1">
          {(['rejected', 'draft', 'approved'] as PairingStatus[]).map(status => {
            const config = PAIRING_STATUS_CONFIG[status]
            const isActive = pairing?.status === status
            return (
              <button
                key={status}
                onClick={() => {
                  const id = ensureRecord()
                  if (id) updatePairingStatus(id, isActive ? 'none' : status)
                }}
                className={`w-8 h-8 text-sm rounded-lg transition-all flex items-center justify-center ${isActive
                  ? `${config.color} shadow-sm`
                  : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                title={status.charAt(0).toUpperCase() + status.slice(1)}
              >
                <StatusIcon status={status} />
              </button>
            )
          })}
        </div>
        <div className="flex items-center gap-1">
          {/* Delete only makes sense when a record exists. Hidden for
              ephemeral pairs since there's nothing to remove. */}
          {pairing && (
            <button
              onClick={handleDeletePairing}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete pairing"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Close pair panel"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Rating row — 1=stretch · 2=so-so · 3=ok · 4=good · 5=great. Click an
          active rating again to clear. Optional; quality is its own axis,
          orthogonal to status. */}
      <div className="px-3 py-2 shrink-0 border-b border-gray-100 flex items-center gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mr-1">
          rating
        </span>
        <div className="flex items-center gap-0.5">
          {([1, 2, 3, 4, 5] as PairingRating[]).map(value => {
            const isActive = pairing?.rating === value
            return (
              <button
                key={value}
                onClick={() => {
                  const id = ensureRecord()
                  if (id) updatePairingRating(id, isActive ? null : value)
                }}
                title={`${value} · ${PAIRING_RATING_LABELS[value]}`}
                className={`w-7 h-7 text-xs rounded-md transition-all flex items-center justify-center font-medium ${
                  isActive
                    ? 'bg-amber-100 text-amber-700 shadow-sm ring-1 ring-amber-300'
                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {value}
              </button>
            )
          })}
        </div>
        {pairing?.rating !== undefined && (
          <span className="text-[11px] text-gray-500 ml-1">
            {PAIRING_RATING_LABELS[pairing.rating]}
          </span>
        )}
      </div>

      {/* Pair display — prominent up top, centered. Clicking letters on a row
          opens that word's panel. */}
      <div className="px-4 py-4 shrink-0 bg-white flex flex-col items-center">
        <CrosswordPair
          textA={lettersA}
          textB={lettersB}
          autoHighlight
          {...(onShowWord && {
            onRowAClick: () => onShowWord(wordA.id),
            onRowBClick: () => onShowWord(wordB.id),
          })}
        />
        <div className="mt-2 text-[11px] text-gray-400">
          {lettersA.length} letters
        </div>
      </div>

      {/* Tags — inline chips with × removal and a + autocomplete affordance.
          Free-form labels; the registry on the store backs the suggestions.
          Adding a tag to an ephemeral pair materializes the record. */}
      <div className="px-4 pb-3 shrink-0">
        <TagEditor
          tags={pairing?.tags ?? []}
          onChange={(tags) => {
            const id = ensureRecord()
            if (id) setPairingTags(id, tags)
          }}
        />
      </div>

      {/* Clue accordion */}
      <SectionHeader
        label="Clue"
        open={openSections.clue}
        onToggle={() => toggleSection('clue')}
        trailing={
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
              isDraft
                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}
          >
            {isDraft ? 'Draft' : 'Final'}
          </span>
        }
      />
      {openSections.clue && (
        <div className="px-4 py-3 bg-white shrink-0 space-y-2.5">
          <input
            type="text"
            value={clueText}
            onChange={e => setClueText(e.target.value)}
            onBlur={() => persistClue(clueText, clueStatus)}
            onKeyDown={e => e.key === 'Enter' && persistClue(clueText, clueStatus)}
            placeholder="Add a clue…"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:border-gray-400 ${
              isDraft && clueText.trim() ? 'italic text-gray-700 border-amber-200' : 'text-gray-900 border-gray-200'
            }`}
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-md">
              {(['draft', 'final'] as ClueStatus[]).map(status => (
                <button
                  key={status}
                  onClick={() => {
                    setClueStatus(status)
                    if (clueText.trim()) persistClue(clueText, status)
                  }}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    clueStatus === status
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {status === 'draft' ? 'Draft' : 'Final'}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (!clueText.trim()) return
                  const id = ensureRecord()
                  if (id) addPairingDraftClue(id, clueText.trim())
                }}
                disabled={!clueText.trim()}
                className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                title="Save current text as a new draft"
              >
                + Save draft
              </button>
            </div>
          </div>

          {/* Saved drafts — only when a real record exists. Ephemeral pairs
              can't have drafts; the array is always empty. */}
          {pairing && (pairing.clueDrafts ?? []).length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400 px-0.5">
                Saved drafts ({pairing.clueDrafts!.length})
              </p>
              <div className="space-y-1">
                {pairing.clueDrafts!.map((draft, idx) => {
                  const isActive = pairing.clue?.id === draft.id ||
                    (!pairing.clue?.id && pairing.clue?.text === draft.text)
                  return (
                    <div
                      key={draft.id ?? `${idx}-${draft.text}`}
                      className={`group flex items-center gap-2 px-2 py-1.5 rounded-md text-sm border transition-colors ${
                        isActive
                          ? 'bg-blue-50 border-blue-200 text-blue-900'
                          : 'bg-gray-50/50 border-gray-100 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <button
                        onClick={() => {
                          setClueText(draft.text)
                          setClueStatus('draft')
                          updatePairingClue(pairing.id, draft.text, 'draft')
                        }}
                        className="flex-1 text-left truncate"
                        title="Use this draft"
                      >
                        {draft.text}
                      </button>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setClueText(draft.text)
                            setClueStatus('final')
                            updatePairingClue(pairing.id, draft.text, 'final')
                          }}
                          className="px-1.5 py-0.5 text-[11px] text-gray-500 hover:text-green-700 hover:bg-green-50 rounded"
                          title="Use as final clue"
                        >
                          Final
                        </button>
                        {draft.id && (
                          <button
                            onClick={() => deletePairingDraftClue(pairing.id, draft.id!)}
                            className="px-1.5 py-0.5 text-[11px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete this draft"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Direct placements section — always shown when there are direct
          spots. Cheap to compute; conflict / adjacency / wildcard /
          ownership rules already enforced by findPlacementOptions. */}
      <SectionHeader
        label={`Placements${placementOptions.length > 0 ? ` (${placementOptions.length})` : ''}`}
        open={openSections.placements}
        onToggle={() => toggleSection('placements')}
        trailing={
          <button
            onClick={onShowCrossings}
            className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
              crossingsOpen
                ? 'bg-blue-100 text-blue-700'
                : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
            }`}
          >
            {crossingsOpen ? 'Hide' : `Show ${crossingsCount} crossings`} →
          </button>
        }
      />
      {openSections.placements && (
        <div className="bg-gray-50/30 p-2">
          {placementOptions.length === 0 ? (
            <p className="text-center py-6 text-gray-400 text-sm">
              {grid && grid.placements.length === 0
                ? 'Place a word on the grid first'
                : 'No direct spots — try Solutions for a placement path'}
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {placementOptions.map(opt => (
                <button
                  key={`${opt.row},${opt.col},${opt.direction}`}
                  onMouseEnter={() => onPreviewChange?.({
                    wordAText: wordA.text,
                    wordBText: wordB.text,
                    row: opt.row,
                    col: opt.col,
                    direction: opt.direction,
                  })}
                  onMouseLeave={() => onPreviewChange?.(null)}
                  onClick={() => {
                    onPlacePair?.(wordA.text, wordB.text, opt.row, opt.col, opt.direction)
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-white rounded-lg hover:bg-blue-50 hover:shadow-sm transition-all text-left"
                >
                  <span className="font-mono text-sm text-gray-600">
                    {opt.direction === 'across' ? '→' : '↓'} ({opt.row + 1}, {opt.col + 1})
                  </span>
                  <span className="text-xs text-gray-400 tabular-nums">
                    {opt.intersectionCount} cross{opt.intersectionCount === 1 ? '' : 'es'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Solutions — backward-search chains, broken into per-depth accordions.
          Each is closed by default and computes lazily when the user clicks
          its header (caches afterwards). */}
      <SectionHeader
        label="Solutions"
        open={openSections.solutions}
        onToggle={() => toggleSection('solutions')}
        trailing={
          <span className="text-[11px] text-gray-400">find a placement path</span>
        }
      />
      {openSections.solutions && (
        <div className="bg-gray-50/30 p-2 space-y-1.5">
          {[1, 2, 3].map(depth => {
            const entry = solutionsByDepth.get(depth)
            return (
              <DepthAccordion
                key={depth}
                depth={depth}
                solutions={entry ? entry.solutions : null}
                truncated={entry ? entry.truncated : false}
                onCompute={() => computeForDepth(depth)}
                targetWordAText={wordA.text}
                targetWordBText={wordB.text}
                onPreviewPlacementsChange={onPreviewPlacementsChange}
                onPreviewSingleChange={onPreviewChange}
                onApply={sol => {
                  if (!pairing) return
                  applySolution(sol, pairing, wordA.text, wordB.text, placeManyOnGrid)
                }}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- helpers below this line ---

function solutionKey(sol: Solution, idx: number): string {
  const helperKey = sol.helpers.map(h => `${h.wordA.id}:${h.wordB.id}@${h.position.row},${h.position.col},${h.position.direction}`).join('|')
  return `${helperKey}=>${sol.target.row},${sol.target.col},${sol.target.direction}#${idx}`
}

function helperToPlacementInput(step: HelperStep): PlacePairInput {
  return {
    pairingId: step.pairing?.id ?? null,
    wordAText: step.wordA.text,
    wordBText: step.wordB.text,
    row: step.position.row,
    col: step.position.col,
    direction: step.position.direction,
    id: generateId(),
  }
}

function targetToPlacementInput(
  pairing: Pairing,
  wordAText: string,
  wordBText: string,
  target: Solution['target'],
): PlacePairInput {
  return {
    pairingId: pairing.id,
    wordAText,
    wordBText,
    row: target.row,
    col: target.col,
    direction: target.direction,
    id: generateId(),
  }
}

function solutionToPreviewPlacements(
  solution: Solution,
  targetWordAText: string,
  targetWordBText: string,
): PreviewPlacement[] {
  const previews: PreviewPlacement[] = solution.helpers.map(h => ({
    wordAText: h.wordA.text,
    wordBText: h.wordB.text,
    row: h.position.row,
    col: h.position.col,
    direction: h.position.direction,
    role: 'helper' as const,
  }))
  previews.push({
    wordAText: targetWordAText,
    wordBText: targetWordBText,
    row: solution.target.row,
    col: solution.target.col,
    direction: solution.target.direction,
    role: 'target' as const,
  })
  return previews
}

function applySolution(
  solution: Solution,
  pairing: Pairing,
  targetWordAText: string,
  targetWordBText: string,
  placeManyOnGrid: (placements: PlacePairInput[]) => { success: boolean; error?: string; placementIds?: string[] },
): void {
  const placements: PlacePairInput[] = [
    ...solution.helpers.map(helperToPlacementInput),
    targetToPlacementInput(pairing, targetWordAText, targetWordBText, solution.target),
  ]
  const result = placeManyOnGrid(placements)
  if (!result.success) {
    alert(`Couldn't apply solution: ${result.error ?? 'unknown'}`)
  }
}

// One row inside a depth accordion: a chain of pair tiles. Status icon next
// to each pair so the user can assess at a glance which solutions involve
// already-rated pairs. Hover previews the chain on the grid; click applies
// the whole chain (helpers + target) atomically.
function SolutionChainRow({
  solution,
  targetWordAText,
  targetWordBText,
  onPreviewPlacementsChange,
  onPreviewSingleChange,
  onApply,
}: {
  solution: Solution
  targetWordAText: string
  targetWordBText: string
  onPreviewPlacementsChange?: ((placements: PreviewPlacement[]) => void) | undefined
  onPreviewSingleChange?: ((preview: PreviewPlacement | null) => void) | undefined
  onApply: () => void
}) {
  const handleEnter = () => {
    onPreviewSingleChange?.(null)
    onPreviewPlacementsChange?.(solutionToPreviewPlacements(solution, targetWordAText, targetWordBText))
  }
  const handleLeave = () => {
    onPreviewPlacementsChange?.([])
  }

  return (
    <button
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={onApply}
      className="w-full flex flex-col items-stretch gap-1 px-2 py-2 bg-white rounded-md hover:bg-blue-50 hover:shadow-sm transition-all"
      title="Apply this whole path as a single action"
    >
      {solution.helpers.map((h, i) => (
        <div key={`${h.wordA.id}:${h.wordB.id}-${i}`} className="flex items-center justify-center gap-1.5">
          <CrosswordPair
            textA={h.wordA.text}
            textB={h.wordB.text}
            autoHighlight
            size="2xs"
          />
          {h.pairing && <StatusBadge status={h.pairing.status} compact />}
        </div>
      ))}
    </button>
  )
}

// One depth accordion: a labeled section ("1-Step" / "2-Step" / "3-Step")
// that runs the search lazily on click. Caches its result via the parent's
// solutionsByDepth map so re-opening is instant.
function DepthAccordion({
  depth,
  solutions,
  truncated,
  onCompute,
  targetWordAText,
  targetWordBText,
  onPreviewPlacementsChange,
  onPreviewSingleChange,
  onApply,
}: {
  depth: number
  solutions: Solution[] | null   // null = not yet computed
  truncated: boolean
  onCompute: () => void
  targetWordAText: string
  targetWordBText: string
  onPreviewPlacementsChange?: ((placements: PreviewPlacement[]) => void) | undefined
  onPreviewSingleChange?: ((preview: PreviewPlacement | null) => void) | undefined
  onApply: (sol: Solution) => void
}) {
  const computed = solutions !== null
  const hasResults = computed && solutions.length > 0

  return (
    <div className="bg-white rounded-md border border-gray-100 overflow-hidden">
      <button
        onClick={onCompute}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors text-left"
        title={depth >= 3 ? 'Deeper searches can take a moment — results are capped to keep the page responsive.' : undefined}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
          {depth}-step
        </span>
        <span className="text-[11px] text-gray-400">
          {!computed ? (depth >= 3 ? 'Search (slow)' : 'Search') : (
            hasResults ? `${solutions.length} found${truncated ? '+' : ''}` : 'none'
          )}
        </span>
      </button>
      {computed && hasResults && (
        <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
          {solutions.map((sol, idx) => (
            <SolutionChainRow
              key={solutionKey(sol, idx)}
              solution={sol}
              targetWordAText={targetWordAText}
              targetWordBText={targetWordBText}
              onPreviewPlacementsChange={onPreviewPlacementsChange}
              onPreviewSingleChange={onPreviewSingleChange}
              onApply={() => onApply(sol)}
            />
          ))}
          {truncated && (
            <p className="text-center py-1 text-[10px] text-amber-600">
              search hit the time limit · more solutions may exist
            </p>
          )}
        </div>
      )}
    </div>
  )
}
